import { z } from "zod";
import { costEstimateEnum, difficultyEnum, mealTypeEnum } from "./recipe";

const importedNutrientValue = z
  .number()
  .finite()
  .min(0)
  .nullable()
  .default(null);

export const importedRecipeIngredientSchema = z.object({
  ingredientId: z.string().uuid(),
  quantity: z.number().positive(),
  unit: z.string().min(1).max(50),
  preparation: z.string().max(100).optional(),
  notes: z.string().max(200).optional(),
  orderIndex: z.number().int().min(0),
  confidence: z.number().min(0).max(1),
  wasCreated: z.boolean(),
  originalText: z.string().min(1),
  matchedName: z.string().min(1),
});

export const importedRecipeSchema = z.object({
  title: z.string().min(3).max(200),
  description: z.string().max(4000).optional(),
  imageUrl: z.string().url().optional(),
  sourceUrl: z.string().url(),
  sourceName: z.string().max(120).optional(),
  prepTimeMinutes: z.number().int().min(0).max(1440),
  cookTimeMinutes: z.number().int().min(0).max(1440),
  servings: z.number().int().min(1).max(20),
  difficulty: difficultyEnum,
  cuisine: z.string().max(100).optional(),
  mealTypes: z.array(mealTypeEnum).min(1),
  tags: z.array(z.string().max(50)).default([]),
  costEstimate: costEstimateEnum,
  instructions: z.array(z.string().min(1).max(2000)).min(1),
  nutrition: z
    .object({
      calories: importedNutrientValue,
      protein: importedNutrientValue,
      carbs: importedNutrientValue,
      fat: importedNutrientValue,
      fiber: importedNutrientValue,
      sodium: importedNutrientValue,
    })
    .optional(),
  qualityScore: z.number().int().min(0).max(100),
  importConfidence: z.number().min(0).max(1),
  needsReview: z.boolean(),
  reviewReasons: z.array(z.string()).default([]),
  ingredients: z.array(importedRecipeIngredientSchema).min(1),
});

export const recipeImportRequestSchema = z.object({
  url: z.string().url(),
  sourceName: z.string().max(120).optional(),
  dryRun: z.boolean().default(false),
});

export type ImportedRecipeInput = z.infer<typeof importedRecipeSchema>;
export type ImportedRecipeIngredientInput = z.infer<
  typeof importedRecipeIngredientSchema
>;
export type RecipeImportRequestInput = z.infer<
  typeof recipeImportRequestSchema
>;

export const recipeImportExtractSchema = z.object({
  url: z.string().url().max(2000),
});
export type RecipeImportExtractInput = z.infer<
  typeof recipeImportExtractSchema
>;
